import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, History } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  fetchScenarioVersion,
  useActivateScenario,
  useSaveScenarioVersion,
  useScenario,
  useScenarioVersions,
} from '@/api/queries'
import type { ScenarioDetail } from '@/api/types'
import { ConfirmDialog } from '@/components/ui/dialog'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Skeleton,
  cn,
} from '@/components/ui/primitives'
import {
  definitionFromDraft,
  draftFromDefinition,
  validateDraft,
  type ScenarioDraft,
} from '@/features/scenarios/draft'
import { StepEditor } from '@/features/scenarios/step-editor'
import { formatDateTime } from '@/shared/format'
import { apiErrorLabel } from '@/shared/labels'

export const Route = createFileRoute('/_auth/scenarios/$scenarioId')({
  component: ScenarioEditPage,
})

/**
 * Загрузку и форму разделяем: редактор монтируется, когда сценарий уже получен,
 * и берёт начальные значения инициализатором useState — синхронизация эффектом
 * давала бы лишний каскад рендеров и мигание формы.
 */
function ScenarioEditPage() {
  const { scenarioId } = Route.useParams()
  const scenario = useScenario(scenarioId)

  if (scenario.isError) {
    return (
      <Card>
        <ErrorState
          message={apiErrorLabel(
            (scenario.error as { detail?: unknown }).detail,
            'Не удалось загрузить сценарий',
          )}
          onRetry={() => void scenario.refetch()}
        />
      </Card>
    )
  }

  if (!scenario.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-52" />
        <Skeleton className="h-40" />
      </div>
    )
  }

  return <ScenarioEditor key={scenarioId} scenario={scenario.data} />
}

function ScenarioEditor({ scenario }: { scenario: ScenarioDetail }) {
  const scenarioId = scenario.id
  const versions = useScenarioVersions(scenarioId)
  const save = useSaveScenarioVersion(scenarioId)
  const activate = useActivateScenario()

  const [draft, setDraft] = useState<ScenarioDraft>(() => draftFromDefinition(scenario.definition))
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null)
  const [confirmActivate, setConfirmActivate] = useState(false)

  // Предупреждение о несохранённых правках: сценарий редактируют подолгу, и
  // потерять получасовую работу по случайному закрытию вкладки обидно.
  useEffect(() => {
    if (!dirty) return
    const handler = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  const updateDraft = (next: ScenarioDraft) => {
    setDraft(next)
    setDirty(true)
  }

  const loadVersion = async (version: number) => {
    try {
      const data = await fetchScenarioVersion(scenarioId, version)
      setDraft(draftFromDefinition(data.definition))
      setLoadedVersion(version)
      setDirty(true)
      toast.info(`Версия ${version} загружена в форму — сохраните, чтобы она стала текущей`)
    } catch (loadError) {
      toast.error(
        apiErrorLabel((loadError as { detail?: unknown }).detail, 'Не удалось загрузить версию'),
      )
    }
  }

  const submit = () => {
    const validationError = validateDraft(draft)
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    save.mutate(definitionFromDraft(draft), {
      onSuccess: (updated) => {
        toast.success(`Сохранено — добавлена версия ${updated.current_version ?? ''}`)
        setDirty(false)
        setLoadedVersion(null)
      },
      onError: (saveError) =>
        setError(apiErrorLabel((saveError as { detail?: unknown }).detail, 'Не удалось сохранить')),
    })
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/scenarios"
            className="inline-flex cursor-pointer items-center gap-1.5 text-[13px] text-ink-muted transition-colors hover:text-ink"
          >
            <ArrowLeft className="size-3.5" />
            Все сценарии
          </Link>
          <h1 className="mt-1.5 font-display text-xl font-bold text-ink">{scenario.name}</h1>
          <p className="mt-0.5 text-[13px] text-ink-subtle">
            Текущая версия: {scenario.current_version ?? '—'}. Сохранение всегда добавляет новую
            версию, прошлые остаются в истории.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {scenario.is_active ? (
            <Badge tone="positive">
              <CheckCircle2 className="size-3.5" />
              Активен
            </Badge>
          ) : (
            <Button size="sm" onClick={() => setConfirmActivate(true)}>
              Сделать активным
            </Button>
          )}
        </div>
      </div>

      {loadedVersion !== null && (
        <p className="rounded-[var(--radius-control)] bg-warning-soft px-3 py-2 text-[13px] text-warning">
          В форму загружена версия {loadedVersion}. Сама по себе она текущей не становится — нажмите
          «Сохранить как новую версию».
        </p>
      )}

      <StepEditor draft={draft} onChange={updateDraft} />

      {error && (
        <p
          role="alert"
          className="rounded-[var(--radius-control)] bg-negative-soft px-3 py-2 text-[13px] text-negative"
        >
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" loading={save.isPending} onClick={submit}>
          Сохранить как новую версию
        </Button>
        {dirty && <span className="text-[13px] text-ink-subtle">Есть несохранённые изменения</span>}
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <History className="size-4 text-ink-subtle" />
              История версий
            </span>
          }
        />
        {versions.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-9" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[13px] text-ink-subtle">
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Версия
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Шагов
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Создана
                  </th>
                  <th scope="col" className="px-5 py-2.5 text-right font-medium">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody>
                {versions.data?.map((version) => {
                  const isCurrent = version.version === scenario.current_version
                  return (
                    <tr
                      key={version.version}
                      className={cn(
                        'border-b border-line/60 transition-colors last:border-0 hover:bg-surface-muted/60',
                      )}
                    >
                      <td className="px-5 py-2.5 font-mono text-[13px] text-ink">
                        {version.version}
                      </td>
                      <td className="px-5 py-2.5 font-mono text-[13px] text-ink-muted">
                        {version.steps_count}
                      </td>
                      <td className="px-5 py-2.5 text-[13px] text-ink-muted">
                        {formatDateTime(version.created_at)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        {isCurrent ? (
                          <Badge tone="brand">Текущая</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void loadVersion(version.version)}
                          >
                            Загрузить в форму
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirmActivate}
        onOpenChange={setConfirmActivate}
        title={`Сделать «${scenario.name}» активным?`}
        description="Движок аналитики сразу начнёт применять этот сценарий к новым разговорам."
        confirmLabel="Сделать активным"
        loading={activate.isPending}
        onConfirm={() =>
          activate.mutate(scenarioId, {
            onSuccess: () => {
              toast.success('Сценарий сделан активным')
              setConfirmActivate(false)
            },
            onError: (activateError) =>
              toast.error(apiErrorLabel((activateError as { detail?: unknown }).detail)),
          })
        }
      />
    </div>
  )
}
